import { AlertProvider, Button, MessagePanel, Toaster } from '@dittolive/anvil'
import { QueryClientProvider } from '@tanstack/react-query'
import { ErrorComponent, isAuthError } from 'components/error'
import { FullPageLoader } from 'components/loader'
import RelayProvider from 'components/relay'
import { TenantContextProvider } from 'components/tenant'
import UserSync from 'components/userSync'
import { withLDProvider } from 'launchdarkly-react-client-sdk'
import { Suspense, useEffect, useMemo } from 'react'
import { safeReturnTo } from 'scenes/login/returnTo'
import { useNavigateRef } from 'utils/navigate'
import {
  isBetterAuthEnabled,
  needsEmailVerification,
  usePortalAuth,
} from 'utils/portalAuth'
import { queryClient } from 'utils/queryClient'

import AppRouter from './AppRouter'
import { AppUpdater } from './AppUpdater'
import AppContext from './context'

/** Entry point for the app, where we handle user authentication. */
function AppEntry() {
  const navigate = useNavigateRef()
  const auth = usePortalAuth()
  const {
    error,
    user,
    isAuthenticated,
    isLoading,
    logout,
    getToken,
    provider,
    refreshSession,
    login,
  } = auth
  const needsVerification = needsEmailVerification(auth)

  useEffect(() => {
    if (!needsVerification) return
    const returnTo = safeReturnTo(
      window.location.pathname + window.location.search + window.location.hash,
    )
    navigate(`/login?verify=1&returnTo=${encodeURIComponent(returnTo)}`)
  }, [needsVerification, navigate])

  useEffect(() => {
    if (!isAuthenticated && !isLoading && !error) {
      if (isBetterAuthEnabled()) {
        const returnTo = safeReturnTo(
          window.location.pathname +
            window.location.search +
            window.location.hash,
        )
        navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`)
        return
      }
      if (window.location.pathname === '/create-account') {
        const searchParams = new URLSearchParams(window.location.search)
        const values = searchParams.get('values')
        login({
          action: 'signup',
          values: values ?? undefined,
          appState: { redirectUrl: '/' },
        })
      } else {
        login({
          action: 'login',
          appState: {
            redirectUrl: window.location.pathname,
            searchParams: window.location.search,
          },
        })
      }
    }
  }, [isAuthenticated, isLoading, login, error, navigate])

  const isUnverifiedEmailError = useMemo(
    () => error?.message?.toLowerCase() === 'email not verified',
    [error],
  )

  const authErrorPrimaryAction = useMemo(
    () => ({ label: 'Log out', onClick: logout }),
    [logout],
  )

  /**
   * An effect to redirect the user to the appropriate page if they have an expired or unverified email.
   *
   * Note: This should ideally handle other error cases, as well. Currently, it only handles the two cases.
   */
  useEffect(() => {
    if (isUnverifiedEmailError) {
      navigate('/email-verification-pending')
    }
  }, [isUnverifiedEmailError, navigate])

  const isAuthComplete = isAuthenticated && !isLoading && !error

  /**
   * An effect to remove any query params from the URL after the user has successfully logged in. Some
   * query params linger after the email confirmation flow, and while they don't cause any issues, they
   * are a bit of an eyesore.
   *
   * This was added as part of a fix for https://github.com/getditto/cloud-services/issues/2322
   */
  useEffect(() => {
    if (!isAuthComplete || isBetterAuthEnabled()) return

    const searchParams = new URLSearchParams(window.location.search)

    const whitelistedParams = new URLSearchParams()
    searchParams.forEach((value, key) => {
      if (PORTAL_QUERY_PARAMS.includes(key)) {
        whitelistedParams.append(key, value)
      }
    })

    if (whitelistedParams.toString().length > 0) {
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}?${whitelistedParams.toString()}${window.location.hash}`,
      )
    } else {
      window.history.replaceState(
        {},
        '',
        window.location.pathname + window.location.hash,
      )
    }
  }, [isAuthComplete])

  if (isLoading || needsVerification) {
    return <FullPageLoader />
  }

  // Auth0 owns its authenticated state; transient token errors keep the app mounted.
  if (!isAuthenticated || (provider === 'better-auth' && error)) {
    if (isBetterAuthEnabled() && provider !== 'auth0' && error) {
      return (
        <div className="mx-auto flex max-w-md flex-col gap-4 p-8">
          <MessagePanel variant="danger" message={error.message} />
          <Button
            onClick={() => {
              if (provider === null) {
                navigate(
                  `/login?returnTo=${encodeURIComponent(safeReturnTo(window.location.pathname + window.location.search + window.location.hash))}`,
                )
              } else {
                void refreshSession().catch(() => {})
              }
            }}
          >
            Try again
          </Button>
          <a
            href={`/login?returnTo=${encodeURIComponent(safeReturnTo(window.location.pathname + window.location.search + window.location.hash))}`}
          >
            Return to login
          </a>
        </div>
      )
    }
    // Show an error page that lets the user log out if there was an authentication error.
    if (isAuthError(error)) {
      return (
        <ErrorComponent error={error} primaryAction={authErrorPrimaryAction} />
      )
    }

    // Let the ErrorFallback handle any other errors.
    if (error) {
      throw error
    }

    return null
  }

  return (
    <QueryClientProvider client={queryClient}>
      <RelayProvider key={`${provider}:${user?.sub}`} token={getToken}>
        <Suspense fallback={<FullPageLoader />}>
          <UserSync user={user!} provider={provider ?? 'auth0'}>
            {(authenticatedUser) => (
              <AlertProvider>
                <AppContext.Provider
                  value={{
                    jwtToken: getToken,
                    isSuperAdmin: !!authenticatedUser?.isSuperAdmin,
                  }}
                >
                  <TenantContextProvider>
                    <AppUpdater />
                    <AppRouter />
                    <Toaster expand visibleToasts={5} />
                  </TenantContextProvider>
                </AppContext.Provider>
              </AlertProvider>
            )}
          </UserSync>
        </Suspense>
      </RelayProvider>
    </QueryClientProvider>
  )
}

export default withLDProvider({
  clientSideID: window._env_.LD_CLIENT_ID,
  options: {
    application: {
      id: 'portal_frontend',
      version: window._env_.BUILD_VERSION,
    },
  },
  // NOTE: We are creating a default, anonymous context here with a fixed key in order to avoid creating
  // an abundance of anonymous contexts. Without this, LaunchDarkly would create an anonymous context with a
  // unique key, which would drive up MAU figures in LaunchDarkly. See https://github.com/getditto/cloud-services/issues/2187
  context: {
    anonymous: true,
    key: 'anonymous-user',
    kind: 'user',
  },
})(AppEntry)

export const PORTAL_QUERY_PARAMS = ['dql', 'collection', 'presence', 'query']
